import {
  defineBaseCommands,
  defineBaseKeymap,
  defineHistory,
  defineKeymap,
  Extension,
  union,
} from "prosekit/core";
import type { Command } from "prosekit/pm/state";
import { defineText } from "prosekit/extensions/text";
import { defineParagraph } from "prosekit/extensions/paragraph";
import { defineBlockquote } from "@prosekit/extensions/blockquote";
import { defineBold } from "@prosekit/extensions/bold";
import { defineCode } from "@prosekit/extensions/code";
import { defineCodeBlock } from "@prosekit/extensions/code-block";
import { defineDoc } from "@prosekit/extensions/doc";
import { defineGapCursor } from "@prosekit/extensions/gap-cursor";
import { defineHardBreak } from "@prosekit/extensions/hard-break";
import { defineHeading } from "@prosekit/extensions/heading";
import { defineHorizontalRule } from "@prosekit/extensions/horizontal-rule";
import { defineImage } from "@prosekit/extensions/image";
import { defineItalic } from "@prosekit/extensions/italic";
import { defineList } from "@prosekit/extensions/list";
import { defineModClickPrevention } from "@prosekit/extensions/mod-click-prevention";
import { defineStrike } from "@prosekit/extensions/strike";
import { defineTable } from "@prosekit/extensions/table";
import { defineUnderline } from "@prosekit/extensions/underline";
import { defineVirtualSelection } from "@prosekit/extensions/virtual-selection";
import { defineLinkSpec, defineLinkCommands } from "prosekit/extensions/link";
import { defineTextColor } from "./extensions/text-color";
import { defineHighlight } from "./extensions/highlight";
import { definePasteConversion } from "./extensions/paste-conversion";
import { defineMarkdownLinkInputRule } from "./extensions/markdown-link";
import { defineBlockImagePaste } from "./extensions/block-image-paste";
import { defineMarkdownImageInputRule } from "./extensions/markdown-image";
import { defineMarkdownAutoConvert } from "./extensions/markdown-auto-convert";
import { defineMention } from "prosekit/extensions/mention";
import { defineReadonly } from "prosekit/extensions/readonly";
import { definePlaceholder } from "prosekit/extensions/placeholder";

type Args = {
  readOnly?: boolean;
  placeholder?: string;
};

// ProseKit's insertHardBreak doesn't call scrollIntoView(), so typing
// Shift+Enter at the bottom edge of the editor doesn't scroll down.
// This keymap overrides the default with a version that scrolls.
function defineHardBreakScrollIntoView() {
  const command: Command = (state, dispatch) => {
    const type = state.schema.nodes.hardBreak;
    if (!type) return false;
    const { $from } = state.selection;
    if (!$from.parent.canReplaceWith($from.index(), $from.index(), type))
      return false;
    if (dispatch) {
      dispatch(state.tr.replaceSelectionWith(type.create()).scrollIntoView());
    }
    return true;
  };

  return defineKeymap({
    "Shift-Enter": command,
    "Mod-Enter": command,
  });
}

function defineCustomLink() {
  return union(
    // Only using those extensions from defineLink() that don't interfere with email addresses.
    // You can add link by pasting or via the link toolbar button.
    // All else failed recently and was rendered as raw markdown in the editor.
    // Plus, it was interfering with email address strings.
    defineLinkCommands(),
    // defineLinkEnterRule(),
    // defineLinkInputRule(),
    // defineLinkMarkRule(),
    // defineLinkPasteRule(),
    defineLinkSpec(),
  );
}

export function defineExtension({ readOnly, placeholder }: Args) {
  const optionalExtensions: Extension[] = [];
  if (readOnly) optionalExtensions.push(defineReadonly());
  if (placeholder)
    optionalExtensions.push(
      definePlaceholder({ placeholder, strategy: "doc" }),
    );

  return union(
    defineDoc(),
    defineText(),
    defineParagraph(),
    defineHeading(),
    defineList(),
    defineBlockquote(),
    defineImage(),
    defineHorizontalRule(),
    defineHardBreak(),
    defineHardBreakScrollIntoView(),
    defineTable(),
    defineCodeBlock(),
    defineItalic(),
    defineBold(),
    defineUnderline(),
    defineStrike(),
    defineCode(),
    defineCustomLink(), // Instead of defineLink(), using a custom set that omits rules that interfere with email links etc.
    defineBaseKeymap(),
    defineBaseCommands(),
    defineHistory(),
    defineGapCursor(),
    defineVirtualSelection(),
    defineModClickPrevention(),
    defineTextColor(),
    defineHighlight(),
    defineMention(),
    definePasteConversion(),
    defineBlockImagePaste(),
    defineMarkdownImageInputRule(),
    defineMarkdownLinkInputRule(),
    defineMarkdownAutoConvert(),
    ...optionalExtensions,
  );
}

export type EditorExtension = ReturnType<typeof defineExtension>;
